// Draft-PO flow — shared by Inventory (per-row Reorder, Plan one order,
// Set up auto-reorder) and Schedule (Reorder event).
//
// Two backend endpoints:
//   POST /api/draft-po          — creates purchase_orders + purchase_order_lines
//   POST /api/po/:id/send       — sends the email + flips status to 'sent'
// Reuses the existing Cases pattern: editable modal, 5s deferred-commit toast
// with Undo, and a visible error banner if Resend rejects the send.

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { IconAlertTriangle, IconShoppingCart, IconX } from '@tabler/icons-react';
import { C, RADIUS } from '../theme';
import { purchaseOrderDraft } from '../lib/emailTemplates';
import { useLang } from '../i18n';
import { supabase } from '../supabase';
import { useOrg } from './OrgContext';
import { apiFetch } from '../lib/api';

export { poDraft, substitutePoNumber };

const UNDO_MS = 5000;
// Stands in for the PO number until the order is actually created, then
// substituted for the real one. It is VISIBLE in the draft the user reads and
// edits, so it cannot stay Italian on an English workspace. It is also a literal
// match key — changing it changes what substitutePoNumber replaces — so it is one
// constant, not a translated string.
const PO_NUMBER_PLACEHOLDER = '[order number]';

const BANNER_PREF_KEY = 'floxir.autoReorderBanner';

function readBannerPref() {
  try {
    return localStorage.getItem(BANNER_PREF_KEY) !== 'off';
  } catch {
    return true;
  }
}

const Ctx = createContext({
  open: () => {},
  version: 0,
  recentOrders: new Map(),
  pendingCount: 0,
  refreshPending: () => {},
  bannerEnabled: true,
  setBannerEnabled: () => {},
});

export function useDraftPO() {
  return useContext(Ctx);
}

// ─── drafted purchase-order email ──────────────────────────────────────────
// Was Italian-only. The body now comes from the shared bilingual template and the
// date, the unit abbreviation and the wording all follow the workspace language.

function poDraft({ supplierName, lines, currency = 'EUR', lang = 'en' }) {
  const dateLong = new Date().toLocaleDateString(lang === 'it' ? 'it-IT' : 'en-GB', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
  const total = lines.reduce(
    (s, l) => s + (Number(l.quantity || 0) * Number(l.unit_price || 0)),
    0
  );
  const itemLines = lines
    .map((l) => {
      const qty = Number(l.quantity || 0);
      const price = Number(l.unit_price || 0);
      const sku = l.sku ? ` (${l.sku})` : '';
      if (price > 0) {
        const lineTotal = qty * price;
        const unit = lang === 'it' ? 'pz' : 'units';
        const at = lang === 'it' ? 'a' : 'at';
        return `- ${l.description}${sku}: ${qty} ${unit} ${at} ${price.toFixed(2)} ${currency} = ${lineTotal.toFixed(2)} ${currency}`;
      }
      return `- ${l.description}${sku}: ${qty} ${lang === 'it' ? 'pz' : 'units'}`;
    })
    .join('\n');
  const { subject, body } = purchaseOrderDraft({
    lang, supplierName, poNumber: PO_NUMBER_PLACEHOLDER, itemLines, total, currency, dateLong,
  });
  return { subject, body };
}

// Drafts saved before the placeholder was translated still carry the old Italian
// token. Replacing both means an in-flight draft sends a real PO number rather than
// the literal text "[numero ordine]" to a supplier.
const LEGACY_PO_NUMBER_PLACEHOLDER = '[numero ordine]';

function substitutePoNumber(text, poNumber) {
  if (!text || !poNumber) return text;
  // global replace without regex pitfalls
  return text
    .split(PO_NUMBER_PLACEHOLDER).join(poNumber)
    .split(LEGACY_PO_NUMBER_PLACEHOLDER).join(poNumber);
}

// ─── provider ──────────────────────────────────────────────────────────────

export function DraftPOProvider({ children }) {
  // Drafted supplier mail follows the workspace language.
  const { lang } = useLang();
  const [ctx, setCtx] = useState(null); // null = modal closed
  const [pending, setPending] = useState(null); // { message, timer, run }
  const [error, setError] = useState(null); // { message }
  const [version, setVersion] = useState(0);
  const [recentOrders, setRecentOrders] = useState(() => new Map());

  // Notification state: how many auto-prepared drafts are waiting for approval,
  // and whether the user has dismissed the banner for the current batch.
  const [pendingCount, setPendingCount] = useState(0);
  const [bannerDismissed, setBannerDismissed] = useState(false);
  // User preference (persisted) — turn the auto-reorder banner off from Settings.
  const [bannerEnabled, setBannerEnabledState] = useState(readBannerPref);
  const prevPendingRef = useRef(0);
  const { orgId: ORG_ID } = useOrg();
  const navigate = useNavigate();
  const location = useLocation();

  const setBannerEnabled = useCallback((on) => {
    setBannerEnabledState(on);
    try {
      localStorage.setItem(BANNER_PREF_KEY, on ? 'on' : 'off');
    } catch {}
  }, []);

  // Single source of truth for the pending count — same query the Inventory
  // "Ready to order" section uses. Drives the nav badge, the Home tile badge,
  // and the banner below, so every surface stays in sync.
  const refreshPending = useCallback(async () => {
    const { count, error: countErr } = await supabase
      .from('purchase_orders')
      .select('id', { count: 'exact', head: true })
      .eq('organization_id', ORG_ID)
      .eq('status', 'draft')
      .in('source', ['auto_prepared', 'voice']);
    if (countErr) return; // keep the prior count rather than fake a 0
    const n = count || 0;
    setPendingCount(n);
    // Re-show the banner when fresh orders appear, or once the queue clears
    // (so the next batch notifies again even if an earlier one was dismissed).
    if (n > prevPendingRef.current || n === 0) setBannerDismissed(false);
    prevPendingRef.current = n;
  }, [ORG_ID]);

  useEffect(() => {
    refreshPending();
  }, [version, refreshPending]);

  const open = useCallback((nextCtx) => {
    setError(null);
    setCtx(nextCtx);
  }, []);

  const clearPending = useCallback(() => {
    setPending((p) => {
      if (p?.timer) clearTimeout(p.timer);
      return null;
    });
  }, []);

  const schedule = useCallback(
    ({ message, run, optimisticOrders }) => {
      // If something else is pending, run it immediately so it isn't lost.
      if (pending?.timer) {
        clearTimeout(pending.timer);
        try {
          pending.run();
        } catch {}
      }
      setError(null);
      const timer = setTimeout(async () => {
        try {
          await run();
          // Record optimistic local order so Inventory shows it immediately.
          if (Array.isArray(optimisticOrders) && optimisticOrders.length > 0) {
            setRecentOrders((cur) => {
              const next = new Map(cur);
              const ts = Date.now();
              for (const o of optimisticOrders) {
                if (!o.sku) continue;
                const prev = next.get(o.sku) || { qty: 0, ts };
                next.set(o.sku, { qty: prev.qty + Number(o.quantity || 0), ts });
              }
              return next;
            });
          }
          setVersion((v) => v + 1);
          setPending(null);
        } catch (err) {
          console.error('[draft-po] pending run failed:', err);
          setPending(null);
          setError({ message: err.message || 'Something went wrong.' });
        }
      }, UNDO_MS);
      setPending({ message, timer, run });
    },
    [pending]
  );

  // ── action: Save as draft ─────────────────────────────────────────────
  const handleSaveDraft = useCallback(
    (form) => {
      const existingPoId = ctx?.existingPoId;
      const source = ctx?.source || 'manual';
      setCtx(null);
      const supplierLabel = form.supplierName ? ` for ${form.supplierName}` : '';
      schedule({
        message: `Saving draft${supplierLabel} · `,
        optimisticOrders: form.lines.map((l) => ({ sku: l.sku, quantity: l.quantity })),
        run: async () => {
          if (existingPoId) {
            // Edit mode: replace the existing draft so the new lines stick.
            await supabase.from('purchase_orders').delete().eq('id', existingPoId);
          }
          const draft = await postDraftPO({ ...form, source, warehouseId: ctx?.warehouseId });
          if (!draft.ok) throw new Error(draft.message || 'Could not save the draft.');
        },
      });
    },
    [schedule, ctx]
  );

  // ── action: Send order ────────────────────────────────────────────────
  const handleSendOrder = useCallback(
    (form) => {
      const existingPoId = ctx?.existingPoId;
      const source = ctx?.source || 'manual';
      setCtx(null);
      schedule({
        message: form.to ? `Sending order to ${form.to} · ` : 'Sending order · ',
        optimisticOrders: form.lines.map((l) => ({ sku: l.sku, quantity: l.quantity })),
        run: async () => {
          if (existingPoId) {
            // Edit mode: replace the existing draft with a fresh one, then send.
            await supabase.from('purchase_orders').delete().eq('id', existingPoId);
          }
          const draft = await postDraftPO({ ...form, source, warehouseId: ctx?.warehouseId });
          if (!draft.ok) throw new Error(draft.message || 'Could not create the order.');
          const po = draft.po;
          const finalSubject = substitutePoNumber(form.subject, po.po_number);
          const finalBody = substitutePoNumber(form.body, po.po_number);
          const send = await postSendPO(po.id, {
            to: form.to,
            subject: finalSubject,
            body: finalBody,
          });
          if (!send.ok) throw new Error(send.message || 'The email could not be sent.');
        },
      });
    },
    [schedule, ctx]
  );

  // ── action: one-tap approve & send (existing draft, no modal) ─────────
  const quickApproveAndSend = useCallback(
    ({ poId, poNumber, supplier, lines, currency }) => {
      if (!supplier?.email) {
        setError({
          message:
            "We don't have an email for this supplier — tap Edit on this prepared order to add it.",
        });
        return;
      }
      const draft = poDraft({
        supplierName: supplier.display_name || supplier.name,
        lines,
        currency: currency || 'EUR',
        lang,
      });
      schedule({
        message: `Sending order to ${supplier.email} · `,
        run: async () => {
          const subject = substitutePoNumber(draft.subject, poNumber);
          const body = substitutePoNumber(draft.body, poNumber);
          const send = await postSendPO(poId, {
            to: supplier.email,
            subject,
            body,
          });
          if (!send.ok) throw new Error(send.message || 'The email could not be sent.');
        },
      });
    },
    [schedule, lang]
  );

  // ── action: dismiss a prepared draft ──────────────────────────────────
  const dismissDraft = useCallback(
    ({ poId, message }) => {
      schedule({
        message: message || 'Dismissing prepared order · ',
        run: async () => {
          const { error: delErr } = await supabase
            .from('purchase_orders')
            .delete()
            .eq('id', poId);
          if (delErr) throw new Error(delErr.message);
        },
      });
    },
    [schedule]
  );

  const value = useMemo(
    () => ({
      open,
      version,
      recentOrders,
      quickApproveAndSend,
      dismissDraft,
      pendingCount,
      refreshPending,
      bannerEnabled,
      setBannerEnabled,
    }),
    [
      open,
      version,
      recentOrders,
      quickApproveAndSend,
      dismissDraft,
      pendingCount,
      refreshPending,
      bannerEnabled,
      setBannerEnabled,
    ]
  );

  return (
    <Ctx.Provider value={value}>
      {children}
      {ctx && (
        <DraftPOModal
          ctx={ctx}
          onCancel={() => setCtx(null)}
          onSend={handleSendOrder}
          onSaveDraft={handleSaveDraft}
        />
      )}
      {pending && <DraftPOToast message={pending.message} onUndo={clearPending} />}
      {error && (
        <ErrorBanner
          message={error.message}
          onDismiss={() => setError(null)}
        />
      )}
      {/* App-wide nudge — skipped on Inventory, where the section is in view. */}
      {bannerEnabled && pendingCount > 0 && !bannerDismissed && location.pathname !== '/inventory' && (
        <PendingOrdersBanner
          count={pendingCount}
          onReview={() => {
            setBannerDismissed(true);
            navigate('/inventory#ready-to-order');
          }}
          onDismiss={() => setBannerDismissed(true)}
        />
      )}
    </Ctx.Provider>
  );
}

// ─── backend helpers ───────────────────────────────────────────────────────

async function postDraftPO({ supplierId, supplierName, supplierEmail, lines, notes, expectedDate, source, warehouseId }) {
  let res;
  try {
    res = await apiFetch('/api/draft-po', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        supplier_id: supplierId || undefined,
        supplier_name: supplierName,
        supplier_email: supplierEmail || undefined,
        expected_date: expectedDate || undefined,
        source: source || undefined,
        warehouse_id: warehouseId || undefined,
        lines: lines.map((l) => ({
          sku: l.sku,
          description: l.description,
          quantity: Number(l.quantity || 0),
          unit_price: l.unit_price === '' || l.unit_price == null ? undefined : Number(l.unit_price),
        })),
        notes: notes || undefined,
      }),
    });
  } catch (err) {
    return { ok: false, message: "The order service could not be reached. Check the connection and retry." };
  }
  if (!res.ok) {
    const payload = await res.json().catch(() => ({}));
    return {
      ok: false,
      message:
        payload?.message ||
        payload?.detail?.message ||
        payload?.error ||
        `Order draft failed (${res.status}).`,
    };
  }
  const payload = await res.json();
  return { ok: true, po: payload.po, supplier: payload.supplier };
}

async function postSendPO(poId, { to, subject, body }) {
  let res;
  try {
    res = await apiFetch(`/api/po/${poId}/send`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ to, subject, body }),
    });
  } catch (err) {
    return { ok: false, message: "The email service could not be reached. Check the connection and retry." };
  }
  if (!res.ok) {
    const payload = await res.json().catch(() => ({}));
    return {
      ok: false,
      message:
        payload?.message ||
        payload?.detail?.message ||
        payload?.error ||
        `Email send failed (${res.status}).`,
    };
  }
  return { ok: true };
}

// ─── modal ─────────────────────────────────────────────────────────────────

function DraftPOModal({ ctx, onCancel, onSend, onSaveDraft }) {
  const { lang } = useLang();
  const initialLines = useMemo(
    () =>
      (ctx.lines || []).map((l, i) => ({
        key: i,
        sku: l.sku || '',
        description: l.description || l.sku || 'Item',
        quantity: l.quantity != null ? String(l.quantity) : '',
        unit_price: l.unit_price != null ? String(l.unit_price) : '',
      })),
    [ctx]
  );

  const [lines, setLines] = useState(initialLines);
  const [to, setTo] = useState(ctx.supplier?.email || '');
  const [currency, setCurrency] = useState(ctx.supplier?.currency_code || 'EUR');
  const [expectedDate, setExpectedDate] = useState(ctx.expectedDate || '');
  const [expectedDateAutoFilled, setExpectedDateAutoFilled] = useState(false);

  // If only a supplier name was passed in, try to enrich with email, currency,
  // and lead_time_days from the suppliers table so the user doesn't have to
  // type them. The expected-delivery field defaults to today + lead time.
  useEffect(() => {
    if (!ctx.supplier?.name) return;
    let cancelled = false;
    (async () => {
      const { data } = await supabase
        .from('suppliers')
        .select('email, currency_code, lead_time_days')
        .ilike('display_name', ctx.supplier.name)
        .maybeSingle();
      if (cancelled || !data) return;
      if (data.email) setTo((cur) => cur || data.email);
      if (data.currency_code) setCurrency(data.currency_code);
      if (data.lead_time_days && !ctx.expectedDate) {
        const d = new Date();
        d.setDate(d.getDate() + Number(data.lead_time_days));
        setExpectedDate((cur) => cur || d.toISOString().slice(0, 10));
        setExpectedDateAutoFilled(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [ctx.supplier?.name, ctx.expectedDate]);

  const draft = useMemo(
    () =>
      poDraft({
        supplierName: ctx.supplier?.name || '',
        lang,
        lines: lines.map((l) => ({
          sku: l.sku,
          description: l.description,
          quantity: Number(l.quantity || 0),
          unit_price: Number(l.unit_price || 0),
        })),
        currency,
      }),
    [lines, ctx.supplier?.name, currency, lang]
  );

  // Body / subject are editable — auto-regenerate only while the user
  // hasn't manually edited them yet.
  const [subject, setSubject] = useState(draft.subject);
  const [body, setBody] = useState(draft.body);
  const [subjectDirty, setSubjectDirty] = useState(false);
  const [bodyDirty, setBodyDirty] = useState(false);

  useEffect(() => {
    if (!subjectDirty) setSubject(draft.subject);
    if (!bodyDirty) setBody(draft.body);
  }, [draft.subject, draft.body, subjectDirty, bodyDirty]);

  const updateLine = (key, patch) =>
    setLines((cur) => cur.map((l) => (l.key === key ? { ...l, ...patch } : l)));

  const total = lines.reduce(
    (s, l) => s + Number(l.quantity || 0) * Number(l.unit_price || 0),
    0
  );

  const canSend = !!to.trim() && lines.some((l) => Number(l.quantity || 0) > 0);
  const canSave = lines.some((l) => Number(l.quantity || 0) > 0);

  const fmtPrice = (n) =>
    n > 0 ? `${currency} ${Number(n).toFixed(2)}` : null;

  const formPayload = () => ({
    supplierId: ctx.supplier?.id,
    supplierName: ctx.supplier?.name,
    supplierEmail: to,
    to,
    subject,
    body,
    expectedDate: expectedDate || null,
    lines: lines.map((l) => ({
      sku: l.sku,
      description: l.description,
      quantity: Number(l.quantity || 0),
      unit_price: l.unit_price === '' ? null : Number(l.unit_price),
    })),
    notes: ctx.notes || null,
  });

  return (
    <div
      onClick={onCancel}
      role="dialog"
      aria-modal="true"
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(44,36,32,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: 24,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: C.card,
          border: `1px solid ${C.border}`,
          borderRadius: RADIUS.lg,
          width: '100%',
          maxWidth: 640,
          maxHeight: 'calc(100vh - 48px)',
          overflowY: 'auto',
          padding: '20px 22px 18px',
          boxShadow: '0 12px 40px rgba(0,0,0,0.18)',
          fontFamily: 'inherit',
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 14,
          }}
        >
          <div>
            <div style={{ fontSize: 16, fontWeight: 500, color: C.textDark }}>
              {ctx.title || 'Draft a purchase order'}
            </div>
            {ctx.supplier?.name && (
              <div style={{ fontSize: 12, color: C.textMuted, marginTop: 3 }}>
                Supplier: {ctx.supplier.name}
              </div>
            )}
          </div>
          <button
            type="button"
            onClick={onCancel}
            aria-label="Close"
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: 4,
              color: C.textMuted,
            }}
          >
            <IconX size={18} stroke={2} />
          </button>
        </div>

        {/* Lines */}
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 11, color: C.textFaint, marginBottom: 6 }}>Items</div>
          <div
            style={{
              border: `1px solid ${C.border}`,
              borderRadius: RADIUS.md,
              overflow: 'hidden',
            }}
          >
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '2fr 80px 110px 90px',
                gap: 8,
                padding: '8px 10px',
                background: C.bg,
                fontSize: 11,
                color: C.textFaint,
                borderBottom: `1px solid ${C.border}`,
              }}
            >
              <span>Item</span>
              <span style={{ textAlign: 'right' }}>Qty</span>
              <span style={{ textAlign: 'right' }}>Unit price</span>
              <span style={{ textAlign: 'right' }}>Total</span>
            </div>
            {lines.map((l) => {
              const lineTotal = Number(l.quantity || 0) * Number(l.unit_price || 0);
              return (
                <div
                  key={l.key}
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '2fr 80px 110px 90px',
                    gap: 8,
                    padding: '10px',
                    alignItems: 'center',
                    borderBottom: `1px solid ${C.border}`,
                    fontSize: 13,
                  }}
                >
                  <div>
                    <div style={{ color: C.textDark, fontWeight: 500 }}>{l.description}</div>
                    {l.sku && <div style={{ fontSize: 11, color: C.textFaint }}>{l.sku}</div>}
                  </div>
                  <input
                    type="number"
                    min="0"
                    value={l.quantity}
                    onChange={(e) => updateLine(l.key, { quantity: e.target.value })}
                    style={numberInputStyle('right')}
                  />
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={l.unit_price}
                    onChange={(e) => updateLine(l.key, { unit_price: e.target.value })}
                    placeholder="—"
                    style={numberInputStyle('right')}
                  />
                  <div
                    style={{
                      textAlign: 'right',
                      color: lineTotal > 0 ? C.textDark : C.textFaint,
                      fontWeight: lineTotal > 0 ? 500 : 400,
                    }}
                  >
                    {fmtPrice(lineTotal) || '—'}
                  </div>
                </div>
              );
            })}
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '2fr 80px 110px 90px',
                gap: 8,
                padding: '10px',
                background: C.bg,
                fontSize: 13,
              }}
            >
              <span style={{ color: C.textFaint }}>Total</span>
              <span />
              <span />
              <span
                style={{
                  textAlign: 'right',
                  color: total > 0 ? C.textDark : C.textFaint,
                  fontWeight: 500,
                }}
              >
                {fmtPrice(total) || '—'}
              </span>
            </div>
          </div>
        </div>

        {/* Recipient + expected delivery */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 180px', gap: 12, marginBottom: 14 }}>
          <div>
            <div style={{ fontSize: 11, color: C.textFaint, marginBottom: 4 }}>
              Send to (supplier email)
            </div>
            <input
              type="email"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              placeholder={ctx.supplier?.email ? '' : 'supplier@example.com'}
              style={inputStyle}
            />
          </div>
          <div>
            <div style={{ fontSize: 11, color: C.textFaint, marginBottom: 4 }}>
              Expected delivery
            </div>
            <input
              type="date"
              value={expectedDate}
              onChange={(e) => {
                setExpectedDate(e.target.value);
                setExpectedDateAutoFilled(false);
              }}
              style={inputStyle}
            />
            {expectedDateAutoFilled && (
              <div style={{ fontSize: 11, color: C.textFaint, marginTop: 3 }}>
                Pre-filled from supplier lead time
              </div>
            )}
          </div>
        </div>

        {/* Subject */}
        <div style={{ marginBottom: 12 }}>
          <div style={{ fontSize: 11, color: C.textFaint, marginBottom: 4 }}>Subject</div>
          <input
            type="text"
            value={subject}
            onChange={(e) => {
              setSubject(e.target.value);
              setSubjectDirty(true);
            }}
            style={inputStyle}
          />
        </div>

        {/* Body */}
        <div style={{ marginBottom: 6 }}>
          <div style={{ fontSize: 11, color: C.textFaint, marginBottom: 4 }}>
            Message (Italian)
          </div>
          <textarea
            value={body}
            onChange={(e) => {
              setBody(e.target.value);
              setBodyDirty(true);
            }}
            rows={12}
            style={{ ...inputStyle, resize: 'vertical', lineHeight: 1.5 }}
          />
        </div>
        <div style={{ fontSize: 11, color: C.textFaint, marginBottom: 16 }}>
          {PO_NUMBER_PLACEHOLDER} is replaced with the real PO number when the order is sent.
        </div>

        {/* Actions */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
          <button
            type="button"
            onClick={onCancel}
            style={outlineBtn(C.textDark)}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => onSaveDraft(formPayload())}
            disabled={!canSave}
            style={{ ...outlineBtn(C.orange), opacity: canSave ? 1 : 0.5 }}
          >
            Save as draft
          </button>
          <button
            type="button"
            onClick={() => onSend(formPayload())}
            disabled={!canSend}
            style={{ ...primaryBtn(), opacity: canSend ? 1 : 0.5 }}
          >
            Send order
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── toast ─────────────────────────────────────────────────────────────────

function DraftPOToast({ message, onUndo }) {
  const [remaining, setRemaining] = useState(UNDO_MS);
  const startRef = useRef(Date.now());
  useEffect(() => {
    const id = setInterval(() => {
      const left = Math.max(0, UNDO_MS - (Date.now() - startRef.current));
      setRemaining(left);
      if (left === 0) clearInterval(id);
    }, 100);
    return () => clearInterval(id);
  }, []);
  return (
    <div
      style={{
        position: 'fixed',
        left: '50%',
        bottom: 28,
        transform: 'translateX(-50%)',
        background: C.textDark,
        color: '#fff',
        padding: '12px 16px',
        borderRadius: RADIUS.md,
        boxShadow: '0 12px 30px rgba(0,0,0,0.25)',
        display: 'flex',
        alignItems: 'center',
        gap: 14,
        fontSize: 13,
        zIndex: 1100,
        minWidth: 320,
      }}
    >
      <span>
        {message}
        <span style={{ opacity: 0.7 }}>{Math.ceil(remaining / 1000)}s</span>
      </span>
      <button
        type="button"
        onClick={onUndo}
        style={{
          background: 'none',
          border: 'none',
          color: C.logoX,
          fontWeight: 500,
          fontSize: 13,
          cursor: 'pointer',
          fontFamily: 'inherit',
        }}
      >
        Undo
      </button>
    </div>
  );
}

// ─── error banner ──────────────────────────────────────────────────────────

function ErrorBanner({ message, onDismiss }) {
  return (
    <div
      role="alert"
      style={{
        position: 'fixed',
        left: '50%',
        bottom: 28,
        transform: 'translateX(-50%)',
        background: C.redLight,
        border: `1px solid ${C.redBorder}`,
        color: C.red,
        padding: '12px 14px',
        borderRadius: RADIUS.md,
        boxShadow: '0 12px 30px rgba(0,0,0,0.18)',
        display: 'flex',
        alignItems: 'flex-start',
        gap: 10,
        fontSize: 13,
        lineHeight: 1.4,
        zIndex: 1100,
        maxWidth: 460,
      }}
    >
      <IconAlertTriangle size={16} stroke={2} style={{ flexShrink: 0, marginTop: 1 }} />
      <div style={{ flex: 1 }}>{message}</div>
      <button
        type="button"
        onClick={onDismiss}
        aria-label="Dismiss"
        style={{
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          color: C.red,
          padding: 2,
        }}
      >
        <IconX size={14} stroke={2} />
      </button>
    </div>
  );
}

// ─── prepared-orders banner ──────────────────────────────────────────────────
// App-wide notification so the user knows Floxir has prepared orders without
// having to open Inventory. Dismissible; re-appears when more are prepared.
function PendingOrdersBanner({ count, onReview, onDismiss }) {
  return (
    <div
      role="status"
      style={{
        position: 'fixed',
        top: 14,
        left: '50%',
        transform: 'translateX(-50%)',
        background: C.amberLight,
        border: `1px solid ${C.amberBorder}`,
        borderRadius: RADIUS.md,
        boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '10px 12px 10px 14px',
        fontSize: 13,
        lineHeight: 1.4,
        zIndex: 1090,
        maxWidth: 'calc(100vw - 32px)',
      }}
    >
      <IconShoppingCart size={18} stroke={2} style={{ flexShrink: 0, color: C.orange }} />
      <span style={{ color: C.textDark }}>
        Floxir prepared <span style={{ fontWeight: 600 }}>{count}</span>{' '}
        {count === 1 ? 'order' : 'orders'} for you to review.
      </span>
      <button
        type="button"
        onClick={onReview}
        style={{
          background: C.orange,
          color: '#fff',
          border: 'none',
          borderRadius: RADIUS.md,
          padding: '6px 12px',
          fontSize: 12,
          fontWeight: 500,
          cursor: 'pointer',
          fontFamily: 'inherit',
          flexShrink: 0,
        }}
      >
        Review
      </button>
      <button
        type="button"
        onClick={onDismiss}
        aria-label="Dismiss"
        style={{
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          color: C.textMuted,
          padding: 2,
          flexShrink: 0,
        }}
      >
        <IconX size={14} stroke={2} />
      </button>
    </div>
  );
}

// ─── shared styles ─────────────────────────────────────────────────────────

const inputStyle = {
  width: '100%',
  padding: '8px 10px',
  border: `1px solid ${C.border}`,
  borderRadius: RADIUS.md,
  fontSize: 13,
  background: C.bg,
  color: C.textDark,
  outline: 'none',
  boxSizing: 'border-box',
  fontFamily: 'inherit',
};

function numberInputStyle(align = 'right') {
  return {
    width: '100%',
    padding: '6px 8px',
    border: `1px solid ${C.border}`,
    borderRadius: RADIUS.md,
    fontSize: 13,
    background: C.card,
    color: C.textDark,
    outline: 'none',
    boxSizing: 'border-box',
    fontFamily: 'inherit',
    textAlign: align,
  };
}

function primaryBtn() {
  return {
    padding: '10px 18px',
    borderRadius: RADIUS.md,
    background: C.orange,
    color: '#fff',
    border: 'none',
    fontSize: 13,
    fontWeight: 500,
    cursor: 'pointer',
    fontFamily: 'inherit',
  };
}

function outlineBtn(color) {
  return {
    padding: '10px 16px',
    borderRadius: RADIUS.md,
    background: C.card,
    border: `1px solid ${C.border}`,
    color,
    fontSize: 13,
    fontWeight: 500,
    cursor: 'pointer',
    fontFamily: 'inherit',
  };
}
